import type { LibraryBlock } from "../build";
import {
  BLACK_GLASS,
  CARD_SHADOW,
  ellipse,
  icon,
  motion,
  rect,
  SHADOW,
  type TemplateNode,
  text,
  WHITE,
} from "./helpers";

// ---------------------------------------------------------------------------
// Zahlen & Fakten: countdown, progress, charts, prices, key figures
// ---------------------------------------------------------------------------

export const NUMBERS: LibraryBlock[] = [
  {
    id: "zahl-countdown",
    title: "Countdown 3-2-1",
    category: "zahlen",
    tags: ["Countdown", "herunterzählen", "Start", "los", "Timer"],
    width: 600,
    height: 600,
    anchor: "center",
    duration: 4000,
    only: "video",
    nodes: [
      ellipse({
        x: 0,
        y: 0,
        w: 600,
        h: 600,
        fill: BLACK_GLASS,
        stroke: { color: "accent", width: 14 },
        name: "Ring",
        ...motion("zoom", { duration: 400 }),
      }),
      ...["3", "2", "1"].map((digit, index) =>
        text({
          text: digit,
          style: "heading-1",
          size: 320,
          color: WHITE,
          x: 50,
          y: 0,
          w: 500,
          h: 600,
          align: "center",
          ...motion("pop", {
            delay: index * 1000,
            until: (index + 1) * 1000,
            duration: 300,
            out: null,
          }),
        }),
      ),
      text({
        text: "LOS!",
        style: "heading-1",
        size: 170,
        color: "accent",
        x: 50,
        y: 0,
        w: 500,
        h: 600,
        align: "center",
        ...motion("pop", { delay: 3000, duration: 350 }),
      }),
    ],
  },
  {
    id: "zahl-fortschritt",
    title: "Fortschrittsbalken",
    category: "zahlen",
    tags: ["Prozent", "Balken", "Statistik", "Bewertung", "Zufriedenheit"],
    width: 900,
    height: 260,
    anchor: "center",
    duration: 4500,
    nodes: [
      text({
        text: "Zufriedenheit",
        style: "subline",
        size: 42,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 0,
        w: 560,
        h: 110,
        ...motion("slide-right"),
      }),
      text({
        text: "94 %",
        style: "heading-1",
        size: 90,
        color: "accent",
        shadow: SHADOW,
        x: 500,
        y: 0,
        w: 400,
        h: 110,
        align: "right",
        ...motion("pop", { delay: 900 }),
      }),
      rect({
        x: 0,
        y: 136,
        w: 900,
        h: 48,
        fill: "#ffffff40",
        radius: "full",
        name: "Leiste",
        ...motion("fade", { delay: 100 }),
      }),
      rect({
        x: 0,
        y: 136,
        w: 846,
        h: 48,
        fill: "accent",
        radius: "full",
        name: "Anteil",
        ...motion("slide-right", { delay: 300, duration: 900 }),
      }),
      text({
        text: "aus 1.200 Bewertungen",
        style: "caption",
        size: 28,
        color: "#ffffffcc",
        shadow: SHADOW,
        x: 0,
        y: 206,
        w: 900,
        ...motion("fade", { delay: 600 }),
      }),
    ],
  },
  {
    id: "zahl-balken",
    title: "Balkendiagramm",
    category: "zahlen",
    tags: ["Diagramm", "Chart", "Statistik", "Umsatz", "Quartal", "Wachstum"],
    width: 900,
    height: 640,
    anchor: "center",
    duration: 5500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 900,
        h: 640,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("pop"),
      }),
      text({
        text: "Umsatz pro Quartal",
        style: "heading-2",
        size: 50,
        color: "text",
        x: 50,
        y: 40,
        w: 800,
        ...motion("fade", { delay: 150 }),
      }),
      rect({
        x: 50,
        y: 540,
        w: 800,
        h: 4,
        fill: "text-muted/40",
        name: "Grundlinie",
        ...motion("fade", { delay: 150 }),
      }),
      ...[
        { label: "Q1", value: "18 T€", height: 180 },
        { label: "Q2", value: "26 T€", height: 260 },
        { label: "Q3", value: "22 T€", height: 220 },
        { label: "Q4", value: "36 T€", height: 330 },
      ].flatMap((bar, index): TemplateNode[] => {
        const x = 80 + index * 200;
        const delay = 350 + index * 220;
        const top = 540 - bar.height;
        return [
          rect({
            x,
            y: top,
            w: 140,
            h: bar.height,
            fill: index === 3 ? "accent" : "primary",
            radius: "sm",
            name: `Balken ${bar.label}`,
            ...motion("slide-up", { delay, duration: 600 }),
          }),
          text({
            text: bar.value.replace(" ", " "),
            style: "subline",
            size: 30,
            color: "text",
            x: x - 20,
            y: top - 50,
            w: 180,
            align: "center",
            ...motion("fade", { delay: delay + 350 }),
          }),
          text({
            text: bar.label,
            style: "subline",
            size: 30,
            color: "text-muted",
            x,
            y: 562,
            w: 140,
            align: "center",
            ...motion("fade", { delay: 200 }),
          }),
        ];
      }),
    ],
  },
  {
    id: "zahl-preis",
    title: "Preis",
    category: "zahlen",
    tags: ["Preis", "Angebot", "Euro", "Sale", "günstig"],
    width: 560,
    height: 330,
    anchor: "center",
    duration: 4000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 560,
        h: 330,
        fill: "background",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Karte",
        ...motion("pop"),
      }),
      text({
        text: "statt 49 €",
        style: "subline",
        size: 36,
        color: "text-muted",
        x: 0,
        y: 40,
        w: 560,
        align: "center",
        ...motion("fade", { delay: 150 }),
      }),
      text({
        text: "29 €",
        style: "heading-1",
        size: 150,
        color: "primary",
        x: 20,
        y: 86,
        w: 520,
        align: "center",
        ...motion("zoom", { delay: 300, duration: 600 }),
      }),
      text({
        text: "inkl. MwSt. · nur diese Woche",
        style: "caption",
        size: 26,
        color: "text-muted",
        x: 20,
        y: 266,
        w: 520,
        align: "center",
        ...motion("fade", { delay: 550 }),
      }),
    ],
  },
  {
    id: "zahl-zeit",
    title: "Zeitangabe",
    category: "zahlen",
    tags: ["Uhr", "Dauer", "Minuten", "Rezept", "Zubereitung", "Timer"],
    width: 620,
    height: 170,
    anchor: "top-left",
    duration: 4000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 620,
        h: 170,
        fill: "background/95",
        radius: "full",
        name: "Pille",
        ...motion("slide-right"),
      }),
      ellipse({
        x: 15,
        y: 15,
        w: 140,
        h: 140,
        fill: "primary",
        name: "Kreis",
        ...motion("pop", { delay: 150 }),
      }),
      icon({
        icon: "timer",
        x: 45,
        y: 45,
        w: 80,
        h: 80,
        color: "on:primary",
        name: "Uhr",
        ...motion("spin", { delay: 150, duration: 700 }),
      }),
      text({
        text: "30 Min.",
        style: "heading-2",
        size: 60,
        color: "text",
        x: 184,
        y: 22,
        w: 400,
        ...motion("slide-right", { delay: 250 }),
      }),
      text({
        text: "Zubereitung",
        style: "subline",
        size: 30,
        color: "text-muted",
        x: 184,
        y: 100,
        w: 400,
        ...motion("slide-right", { delay: 350 }),
      }),
    ],
  },
  {
    id: "zahl-kennzahlen",
    title: "Drei Kennzahlen",
    category: "zahlen",
    tags: ["Zahlen", "Fakten", "Statistik", "Erfahrung", "Projekte", "Agentur"],
    width: 1000,
    height: 300,
    anchor: "center",
    duration: 5000,
    nodes: [
      ...[
        { value: "10+", label: "Jahre Erfahrung" },
        { value: "250", label: "Projekte" },
        { value: "4,9", label: "Sterne bei Google" },
      ].flatMap((figure, index): TemplateNode[] => {
        const x = index * 340;
        const delay = index * 250;
        return [
          text({
            text: figure.value,
            style: "heading-1",
            size: 120,
            color: "accent",
            shadow: SHADOW,
            x,
            y: 0,
            w: 320,
            align: "center",
            ...motion("pop", { delay }),
          }),
          text({
            text: figure.label,
            style: "subline",
            size: 34,
            color: WHITE,
            shadow: SHADOW,
            x: x + 10,
            y: 160,
            w: 300,
            align: "center",
            ...motion("slide-up", { delay: delay + 150 }),
          }),
        ];
      }),
      ...[330, 670].map((x, index) =>
        rect({
          x: x - 2,
          y: 40,
          w: 4,
          h: 200,
          fill: "#ffffff66",
          radius: "full",
          name: "Trenner",
          ...motion("fade", { delay: 150 + index * 250 }),
        }),
      ),
    ],
  },
  {
    id: "zahl-wachstum",
    title: "Wachstum",
    category: "zahlen",
    tags: ["Plus", "Prozent", "Reichweite", "Erfolg", "Trend", "steigend"],
    width: 900,
    height: 400,
    anchor: "center",
    duration: 4500,
    nodes: [
      icon({
        icon: "trend-up",
        x: 360,
        y: 0,
        w: 180,
        h: 180,
        color: "accent",
        shadow: SHADOW,
        name: "Kurve",
        ...motion("fly-up", { duration: 700 }),
      }),
      text({
        text: "+240 %",
        style: "heading-1",
        size: 150,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 170,
        w: 900,
        align: "center",
        ...motion("pop", { delay: 250 }),
      }),
      text({
        text: "mehr Reichweite in 3 Monaten",
        style: "subline",
        size: 38,
        color: "#ffffffdd",
        shadow: SHADOW,
        x: 0,
        y: 346,
        w: 900,
        align: "center",
        ...motion("slide-up", { delay: 500 }),
      }),
    ],
  },
];
